import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Instagram, Facebook } from 'lucide-react';
import { Button } from './ui/button';

export const FinalCTA = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToWork = () => {
    const element = document.getElementById('work');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      data-testid="final-cta-section"
      className="py-24 md:py-32 px-6 lg:px-8 relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 dot-pattern opacity-30" />
      
      <div className="max-w-4xl mx-auto relative z-10">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          {/* Headline */}
          <h2
            data-testid="final-cta-title"
            className="font-['Sora'] text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-foreground tracking-tight"
          >
            Let's Build Something
            <span className="text-primary"> Great</span>
          </h2>

          {/* Subtitle */}
          <p className="text-lg text-muted-foreground mt-6 max-w-2xl mx-auto">
            Ready to elevate your brand? Let's create something that stands out, connects with your audience, and drives results.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
            <Link to="/start-project" onClick={scrollToTop}>
              <Button
                data-testid="final-cta-start-project-btn"
                className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-8 py-6 text-base btn-shine"
              >
                Start a Project
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Button
              onClick={scrollToWork}
              data-testid="final-cta-view-work-btn"
              variant="outline"
              className="rounded-full px-8 py-6 text-base border-border/50 hover:border-primary hover:text-primary transition-all"
            >
              View Work
            </Button>
          </div>

          {/* Contact Options */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mt-8 pt-8 border-t border-border/30">
            <p className="text-sm text-muted-foreground">Or reach out directly:</p>
            <div className="flex items-center gap-4">
              <a
                href="https://instagram.com/damiandepasquale.studio"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-instagram-dm"
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                <Instagram className="w-4 h-4" />
                DM on Instagram
              </a>
              <a
                href="https://www.facebook.com/damiandepasquale.studio"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-facebook-dm"
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                <Facebook className="w-4 h-4" />
                Message on Facebook
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FinalCTA;
